import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import "../styles/style.css";
import ApiService from "../api/apiService";
import ChatSideBarItem from "./chat-sidebar-item";
import MessageHead from "./message-head";
import MessageSeparator from "./message-separator";
import MessageSender from "./message-sender";
import MessageReceiver from "./message-receiver";
import showSwal from "./swal";
import ConversationModal from "./create-conversation-modal";
import PreviewImageModal from "./preview-image-modal";
import SearchChatModal from "./search-chat-modal";
import ViewMembersModal from "./view-members-modal";
import ViewConversationFilesModal from "./view-conversation-files-modal";
import ViewActivityLogsModal from "./view-activity-logs";
import ChatInputReply from "./chat-input-reply";
import buildHubLogo from "../images/bg.png";
import "font-awesome/css/font-awesome.min.css";
import showNotification from "../hooks/user-notification";
import { common } from "../util/common";
import { WebSocketContext, ChatContext } from "../context/contexts";
import loader2 from "../images/loader_2.gif";
import loader3 from "../images/loader_3.gif";
import autosize from "autosize";
import ChangePasswordModal from "./change-password-modal";
import { formatDate } from "../util/helperFunctions";
import EditConversationModal from "./edit-conversation-modal";
import ViewThreadModal from "./view-thread-modal";
import notifSound from "../sounds/notif-sound.mp3";
import SearchInput from "./reusable/search-input";
import SearchResults, { ConversationSearchResult } from "./search-results";
import InfiniteScroll from "react-infinite-scroll-component";
import ImageViewer from "./reusable/image-viewer";
import { useSearchParams } from "react-router-dom";
import MessageTyping from "./message-typing";
import ReactionOptions from "./reusable/reaction-options";
import ToggleSwitch from "./reusable/toggle-switch";
import PinnedMessage from "./pin-message";
import PinModal from "./pin-message-modal";
import SidebarHeader from "./sidebar-header";
import BuildhubTextLogo from "../images/buildhub.ph.png";
import SendPrivateMessageModal from "./send-private-message";
import { resizeFile } from "../hooks/resizeImage";
import ViewReactionsModal from "./view-reactions-modal";
import AiChatbot from "./ai-chatbot";
import AITasksPanel from "./ai-tasks";
import { useAITasks } from "../hooks/useAITasks";
import useMessageJumpWindow from "../hooks/useMessageJumpWindow";
import "./ai-tasks/ai-tasks.css";
import AICommandHints, { TigerZeroHints } from "./ai-command-hints";
import { MentionComposerField, MentionPicker } from "./mention-picker";
import { useMentionComposer } from "../hooks/useMentionComposer";

// Group chat where the exclusive @AI command is available
// TODO: Replace with the actual conversation ID
const AI_CHAT_CONVERSATION_IDS = [
  process.env.REACT_APP_AI_CHAT_CONVERSATION_IDS,
];

function Main(props) {
  // WS
  const WS = useContext(WebSocketContext);
  // WS
  const Chat = useContext(ChatContext);

  const fileInputRef = useRef(null);
  const msgInputTextAreaRef = useRef(null);

  const {
    userDetails,
    linkConversation,
    showHideChangePasswordModal,
    viewChangePasswordModal,
    setConversationLimit,
    conversationsCount,
  } = props;
  let userId;
  if (userDetails && userDetails.id) {
    userId = userDetails.id;
  }

  const [error, setError] = useState(null);
  const [messageContent, setMessageContent] = useState("");

  const [conversationModalShow, setConversationModalShow] = useState(false);
  const [editConversationModalShow, setEditConversationModalShow] =
    useState(false);
  const [searchModalShow, setSearchModalShow] = useState(false);

  const [viewMembersModal, setViewMembersModalShow] = useState(false);
  const [viewConversationFilesModal, setViewConversationFilesModalShow] =
    useState(false);
  const [viewActivityLogsShow, setViewActivityLogsShow] = useState(false);

  const [selectedFile, setSelectedFile] = useState([]);
  const [filePreview, setFilePreview] = useState([]);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const [isAtBottom, setIsAtBottom] = useState(true);

  const [isShowChatInputReply, setIsShowChatInputReply] = useState(false);

  const [selectedMentionData, setSelectedMentionData] = useState([]);
  const mentionSelectRef = useRef(() => {});
  const mention = useMentionComposer({
    messageContent,
    setMessageContent,
    setSelectedMentionData,
    textareaRef: msgInputTextAreaRef,
    members: Chat.conversationMembersData,
    includeAiCommand:
      AI_CHAT_CONVERSATION_IDS.includes(Chat.storedConversationId) &&
      !!userDetails?.admin_access,
    onlineUsers: Chat.onlineUsers,
    onSelectOption: (option) => mentionSelectRef.current(option),
  });

  const [isConnectedRoom, setConnectedRoom] = useState(false);

  const [optimisticMessages, setOptimisticMessages] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [isShowingSearchResults, setIsShowingSearchResults] = useState(false);
  const [globalSearchResults, setGlobalSearchResults] = useState(null);
  const [isGlobalSearchLoading, setIsGlobalSearchLoading] = useState(false);
  const [hasGlobalSearchExecuted, setHasGlobalSearchExecuted] = useState(false);

  const [openLightbox, setOpenLightbox] = useState(false);
  const [lightboxSrcs, setLightboxSrcs] = useState([]);
  const [lightboxActiveIndex, setLightboxActiveIndex] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    messages: jumpMessages,
    isJumpMode,
    hasOlder,
    hasNewer,
    isLoadingOlder: isJumpLoadingOlder,
    isLoadingNewer: isJumpLoadingNewer,
    jumpToMessage,
    loadOlder,
    loadNewer,
    resetJumpWindow,
  } = useMessageJumpWindow({
    scrollContainerRef: Chat.messageMainRef,
  });

  const [isAbleToLoad, setAbleToLoad] = useState(true);
  const isAbleToLoadRef = useRef(true);
  useEffect(() => {
    isAbleToLoadRef.current = isAbleToLoad;
  }, [isAbleToLoad]);

  // Always points to the current handleViewMore — used by the stale scroll listener closure
  const handleViewMoreRef = useRef(null);

  const [showReactionOptionList, setShowReactionOptionList] = useState(false);

  const wsStatus = Chat.wsConnectionState?.status || "disconnected";
  const isRealtimeSyncing = Chat.isRealtimeSyncing;
  const shouldShowWsBanner = wsStatus !== "connected" || isRealtimeSyncing;
  const wsStatusText = isRealtimeSyncing
    ? "Restoring missed messages..."
    : wsStatus === "reconnecting"
    ? `Reconnecting${Chat.wsConnectionState?.attempt ? ` (attempt ${Chat.wsConnectionState.attempt})` : ""}...`
    : wsStatus === "connecting"
    ? "Connecting to realtime updates..."
    : wsStatus === "failed"
    ? "Realtime connection failed. Retrying..."
    : "Realtime connection lost. Waiting to reconnect...";
  const wsStatusClassName = isRealtimeSyncing
    ? "ws-status-banner ws-status-banner--syncing"
    : wsStatus === "reconnecting" || wsStatus === "connecting"
    ? "ws-status-banner ws-status-banner--reconnecting"
    : "ws-status-banner ws-status-banner--offline";

  useEffect(() => {
    let timeoutId;

    if (isJumpMode) {
      setAbleToLoad(hasOlder && !isJumpLoadingOlder);
      return undefined;
    }

    if (Chat.isLoading) {
      setAbleToLoad(false);
    } else {
      timeoutId = setTimeout(() => setAbleToLoad(Chat.hasMoreMessages), 500);
    }

    return () => clearTimeout(timeoutId);
  }, [
    Chat.isLoading,
    Chat.hasMoreMessages,
    hasOlder,
    isJumpLoadingOlder,
    isJumpMode,
  ]);

  const [divHeight, setDivHeight] = useState("83vh");

  useEffect(() => {
    const calculateHeight = () => {
      const windowHeight = window.innerHeight;
      const calculatedHeight = windowHeight * 0.65; // 60% of the screen height
      setDivHeight(`${calculatedHeight}px`);
    };

    // Calculate height on initial load
    calculateHeight();

    // Recalculate height on window resize
    window.addEventListener("resize", calculateHeight);

    // Cleanup event listener on unmount
    return () => window.removeEventListener("resize", calculateHeight);
  }, []);

  useEffect(() => {
    if (msgInputTextAreaRef.current) {
      autosize(msgInputTextAreaRef.current);
      return () => autosize.destroy(msgInputTextAreaRef.current);
    }
  }, []);

  useEffect(() => {
    if (!linkConversation) return;

    const message_id = searchParams.get("q");
    const thread_message_id = searchParams.get("_t");
    const thread_reply = searchParams.get("_tr");

    let observer = null;
    let fallbackTimer = null;
    let pulseTimer = null;
    let cancelled = false;

    const highlightAndScroll = (element) => {
      const span = element.querySelector("span");
      if (span) {
        span.classList.add("pulse");
        pulseTimer = setTimeout(() => {
          if (!cancelled) span.classList.remove("pulse");
        }, 3000);
      }
      // rAF ensures the browser has finished layout before issuing the scroll,
      // preventing off-by-one positions when images/embeds haven't loaded yet.
      requestAnimationFrame(() => {
        if (!cancelled) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    };

    const tryFindAndScroll = (targetId) => {
      const el = document.getElementById(targetId);
      if (!el) return false;
      highlightAndScroll(el);
      return true;
    };

    // Watches the message container for the target element to appear in the DOM.
    // This replaces the hardcoded setTimeout and is reliable for async renders.
    const watchForTarget = (targetId) => {
      if (tryFindAndScroll(targetId)) return;

      const container = Chat.messageMainRef?.current ?? document.body;
      observer = new MutationObserver(() => {
        if (tryFindAndScroll(targetId)) {
          observer.disconnect();
          clearTimeout(fallbackTimer);
        }
      });
      observer.observe(container, { childList: true, subtree: true });

      // Safety valve: disconnect after 10s if the element never appears.
      fallbackTimer = setTimeout(() => {
        observer?.disconnect();
      }, 10_000);
    };

    const runConversationLoad = async () => {
      await Chat.setConversationDetail(linkConversation);
      if (cancelled) return;

      if (!message_id) {
        resetJumpWindow();
        return;
      }

      const anchorMessageId =
        thread_message_id && thread_reply === "true"
          ? thread_message_id
          : message_id;

      const jumpResult = await jumpToMessage(linkConversation, anchorMessageId);

      if (cancelled) return;

      if (!jumpResult?.success) {
        resetJumpWindow();
        if (jumpResult?.reason === "not-found") {
          showSwal("Message not found in this conversation");
        }
        return;
      }

      if (thread_message_id && thread_reply === "true") {
        Chat.handleThreadViewMsgId(thread_message_id, linkConversation);
      }

      // One animation frame lets React's batch of state updates reach the DOM
      // before we start watching for the element.
      requestAnimationFrame(() => {
        if (!cancelled) watchForTarget(String(jumpResult.targetId));
      });
    };

    runConversationLoad();

    return () => {
      cancelled = true;
      observer?.disconnect();
      clearTimeout(fallbackTimer);
      clearTimeout(pulseTimer);
    };
  }, [jumpToMessage, linkConversation, resetJumpWindow, searchParams]);

  const showSearchResults = () => {
    setIsShowingSearchResults(true);
  };
  const hideSearchResults = () => {
    setIsShowingSearchResults(false);
  };

  const handleExitJumpMode = async () => {
    resetJumpWindow();

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("q");
    nextSearchParams.delete("_q");
    nextSearchParams.delete("_t");
    nextSearchParams.delete("_tr");
    nextSearchParams.delete("i");
    setSearchParams(nextSearchParams);

    if (linkConversation) {
      await Chat.setConversationDetail(linkConversation);
      return;
    }

    window.location.reload();
  };

  const handleMessageInput = (event) => {
    const input = event.target.value;

    if (showAiCommandHints) {
      const afterMention = input.replace(
        /^[\s\S]*@@Tiger ZERO AI Command@@\s*/,
        "",
      );
      if (afterMention.trim().length > 0) {
        setShowAiCommandHints(false);
      }
    }

    if (showTigerZeroHints) {
      const afterMention = input.replace(/^[\s\S]*@@Tiger ZERO AI@@\s*/, "");
      if (afterMention.trim().length > 0) {
        setShowTigerZeroHints(false);
      }
    }

    mention.handleChange(event);
  };

  const handleCurrentlyTyping = (e) => {
    WS.websocketInstance.currentlyTyping(
      userDetails.id,
      userDetails.display_name,
      userDetails.profile_photo,
      Chat.storedConversationId,
    );
  };

  const handleSelectAiHint = (template) => {
    const base = "@@Tiger Zero AI Command@@ ";
    const idx = messageContent.indexOf(base);
    const newMsg =
      idx !== -1
        ? messageContent.substring(0, idx + base.length) + template
        : base + template;
    setMessageContent(newMsg);
    setShowAiCommandHints(false);
    setTimeout(() => {
      const textarea = document.querySelector(".js-message--input");
      if (!textarea) return;
      textarea.focus();
      const firstOpen = newMsg.indexOf("[");
      const firstClose = newMsg.indexOf("]", firstOpen);
      if (firstOpen !== -1 && firstClose !== -1) {
        textarea.setSelectionRange(firstOpen, firstClose + 1);
      }
    }, 0);
  };

  const handleSelectTigerHint = (template) => {
    const base = "@@Tiger ZERO AI@@ ";
    const idx = messageContent.indexOf(base);
    const newMsg =
      idx !== -1
        ? messageContent.substring(0, idx + base.length) + template
        : base + template;
    setMessageContent(newMsg);
    setShowTigerZeroHints(false);
    setTimeout(() => {
      const textarea = document.querySelector(".js-message--input");
      if (!textarea) return;
      textarea.focus();
      const firstOpen = newMsg.indexOf("[");
      const firstClose = newMsg.indexOf("]", firstOpen);
      if (firstOpen !== -1 && firstClose !== -1) {
        textarea.setSelectionRange(firstOpen, firstClose + 1);
      }
    }, 0);
  };

  const toggleShowChatReply = () => {
    setIsShowChatInputReply((prevState) => !prevState);
  };

  const checkScrollPosition = () => {
    const messageMain = Chat.messageMainRef.current;
    if (!messageMain) return;
    const { scrollTop, scrollHeight, clientHeight } = messageMain;

    const atBottom = scrollHeight - clientHeight <= scrollTop + 50;
    setIsAtBottom(atBottom);
    Chat.setIsAtBottom(atBottom);

    // Trigger older-message load when user scrolls within 200px of the top.
    // Reads refs so this stale closure always sees current values.
    if (scrollTop <= 200 && isAbleToLoadRef.current) {
      handleViewMoreRef.current?.();
    }
  };

  const scrollToBottom = () => {
    const messageMain = Chat.messageMainRef.current;
    if (messageMain) {
      messageMain.scrollTo({
        top: messageMain.scrollHeight - messageMain.clientHeight,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const messageMain = Chat.messageMainRef.current;
    if (messageMain) {
      messageMain.addEventListener("scroll", checkScrollPosition);
      return () => {
        messageMain.removeEventListener("scroll", checkScrollPosition);
      };
    }
  }, []);

  const closeModal = () => {
    setIsPreviewOpen(false);
    setTimeout(() => setPreviewImage(null), 300);
  };

  const handleImageClick = (imageSrc, activeIndex = 0) => {
    const imgSrc = Array.isArray(imageSrc) ? [...imageSrc] : [imageSrc];
    setLightboxSrcs(imgSrc);
    setLightboxActiveIndex(activeIndex);
    setOpenLightbox(true);
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    const maxFileSizeMB = process.env.REACT_APP_MAX_ATTACHMENT_SIZE_IN_MB;

    const newFilePreviews = [];
    const newSelectedFiles = [];

    if (files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const fileSizeMB = files[i].size / 1024 / 1024;
        if (fileSizeMB > maxFileSizeMB) {
          showSwal({
            title: "Something went wrong",
            icon: "error",
            text: `File ${files[i].name} exceeds 15MB and will not be uploaded.`,
            confirmButtonText: "OK",
          });
          continue;
        }
        const objProperty = {
          filePreview: URL.createObjectURL(files[i]),
          file: files[i],
          type: files[i].type,
        };
        newSelectedFiles.push(objProperty);
        newFilePreviews.push(objProperty);
      }
      setSelectedFile(newSelectedFiles);
      setFilePreview(newFilePreviews);
    }
  };

  const handleSendMessage = async () => {
    if (!Chat.storedConversationId)
      return showSwal(common.errors.INVALID_CONVERSATION);

    const trimmedMessageContent = messageContent.trim();
    const selectedFileSnapshot = [...selectedFile];

    if (trimmedMessageContent.length === 0 && selectedFileSnapshot.length === 0) {
      return;
    }

    const _messageContent =
      trimmedMessageContent.length > 0 ? messageContent : " ";
    const selectedMentionDataSnapshot = [...selectedMentionData];
    const chatReplyDataSnapshot = Chat.chatReplyData;
    const conversationId = Chat.storedConversationId;
    const optimisticId = `temp-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 9)}`;

    const optimisticMessage = {
      id: optimisticId,
      conversation_id: conversationId,
      sender_id: userId,
      content: _messageContent,
      createdAt: new Date().toISOString(),
      is_deleted: false,
      is_pinned: false,
      message_attachment: [],
      reply_to_message_detail: chatReplyDataSnapshot
        ? {
            id: chatReplyDataSnapshot.chat_id,
            content: chatReplyDataSnapshot.message,
            is_deleted: false,
            user: {
              display_name: chatReplyDataSnapshot.display_name,
            },
          }
        : null,
      thread_replies: [],
      message_reactions: [],
      message_reads: [],
      user: {
        id: userDetails?.id,
        display_name: userDetails?.display_name,
        profile_photo: userDetails?.profile_photo,
        email_address: userDetails?.email_address,
      },
      is_optimistic_pending: true,
    };

    setOptimisticMessages((prevState) => [...prevState, optimisticMessage]);

    // Wait for React to paint the new optimistic message before scrolling.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    });

    // Clear composer immediately so users can send the next message while this one is still in flight.
    setMessageContent("");
    setFilePreview([]);
    setSelectedFile([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setSelectedMentionData([]);
    mention.reset();
    Chat.setChatReplyData(null);
    setShowAiCommandHints(false);
    setShowTigerZeroHints(false);

    try {
      const formData = new FormData();

      if (selectedFileSnapshot.length > 0) {
        for (const fileObj of selectedFileSnapshot) {
          const resizedFile = await resizeFile(fileObj.file, 800, 800); // Set max dimensions
          formData.append(
            "message_attachments",
            resizedFile,
            fileObj.file.name,
          );
        }
      }

      formData.append(
        "data",
        JSON.stringify({
          message: _messageContent,
          reply_to_message_id: chatReplyDataSnapshot
            ? chatReplyDataSnapshot.chat_id
            : null,
          mention_data: selectedMentionDataSnapshot,
        }),
      );

      const messageDetail = await ApiService.message.sendMessage(
        formData,
        conversationId,
      );
      const { success } = messageDetail;

      if (success) {
        setOptimisticMessages((prevState) =>
          prevState.map((messageItem) =>
            messageItem.id === optimisticId
              ? { ...messageDetail.message, is_optimistic_pending: false }
              : messageItem,
          ),
        );

        if (/@@Tiger ZERO AI@@/.test(_messageContent)) {
          showAITaskToast("Task created and assigned to Tiger ZERO AI!");
        }
      } else {
        setOptimisticMessages((prevState) =>
          prevState.filter((messageItem) => messageItem.id !== optimisticId),
        );
        showSwal(common.errors.SEND_MESSAGE_ERROR);
      }
    } catch (error) {
      setOptimisticMessages((prevState) =>
        prevState.filter((messageItem) => messageItem.id !== optimisticId),
      );
      showSwal(common.errors.SEND_MESSAGE_ERROR);
      console.error(error);
      setError(error);
    }
  };

  const showHideConversationModal = () => {
    setConversationModalShow(!conversationModalShow);
  };
  const showHideEditConversationModal = () => {
    setEditConversationModalShow(!editConversationModalShow);
  };

  const showHideSearchmodal = () => {
    setSearchModalShow(!searchModalShow);
  };

  const showHideViewMembersModal = () => {
    setViewMembersModalShow(!viewMembersModal);
  };

  const showHideViewConversationFilesModal = () => {
    setViewConversationFilesModalShow(!viewConversationFilesModal);
  };

  const showHideViewActivityLogsModal = () => {
    setViewActivityLogsShow(!viewActivityLogsShow);
  };

  const handleViewMore = async () => {
    if (isJumpMode) {
      await loadOlder();
      return;
    }

    await Chat.updateConversationData("", true, 0);
  };
  handleViewMoreRef.current = handleViewMore;

  const conversationHandleViewMore = () => {
    setConversationLimit((prevLimit) => prevLimit + 5);
  };

  useEffect(() => {
    const handleResize = () => {
      const maxWidth =
        120 * parseFloat(getComputedStyle(document.documentElement).fontSize);
      Chat.setMobileMode(window.innerWidth <= maxWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const conversationProp = props.conversations ? props.conversations : [];
  const restMessages = isJumpMode
    ? jumpMessages
    : Chat.messagesData
    ? Chat.messagesData.messages
    : [];
  const wsMessages = Chat.wsMessagesData ?? [];
  const activeConversationId = Chat.storedConversationId;
  const wsMessagesForActiveConversation = wsMessages.filter(
    (messageItem) => messageItem.conversation_id === activeConversationId,
  );
  const activeOptimisticMessages = optimisticMessages.filter(
    (messageItem) => messageItem.conversation_id === activeConversationId,
  );
  const restIds = new Set(restMessages.map((m) => m.id));
  const optimisticIds = new Set(activeOptimisticMessages.map((m) => m.id));
  const dedupedWsMessages = wsMessagesForActiveConversation.filter(
    (m) => !restIds.has(m.id) && !optimisticIds.has(m.id),
  );
  const dedupedOptimisticMessages = activeOptimisticMessages.filter(
    (m) => !restIds.has(m.id),
  );
  const allItems = [...restMessages]
    .reverse()
    .concat(dedupedWsMessages)
    .concat(dedupedOptimisticMessages)
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  useEffect(() => {
    if (restMessages.length === 0) return;

    const restMessageIds = new Set(restMessages.map((m) => m.id));
    setOptimisticMessages((prevState) =>
      prevState.filter(
        (messageItem) =>
          !(
            messageItem.conversation_id === Chat.storedConversationId &&
            restMessageIds.has(messageItem.id)
          ),
      ),
    );
  }, [restMessages, Chat.storedConversationId]);

  const pinnedConversations = conversationProp.filter(
    (conversation) => conversation.is_pinned,
  );
  const unpinnedConversations = conversationProp.filter(
    (conversation) => !conversation.is_pinned,
  );
  pinnedConversations.sort((a, b) => {
    const dateA = new Date(a.last_message.updatedAt);
    const dateB = new Date(b.last_message.updatedAt);

    return dateB - dateA;
  });

  unpinnedConversations.sort((a, b) => {
    const dateA = new Date(a.last_message.updatedAt);
    const dateB = new Date(b.last_message.updatedAt);

    return dateB - dateA;
  });
  let totalUnreadCount = 0;
  if (conversationProp.length > 0) {
    conversationProp.map((chatDetail) => {
      totalUnreadCount += parseInt(chatDetail.unread_count);
    });
  }

  const activeConversationName =
    Chat.conversationData?.conversation?.name ||
    conversationProp.find((conversation) => conversation.id === Chat.storedConversationId)
      ?.name ||
    "";

  const prevUnreadCountRef = useRef(0);

  useEffect(() => {
    const previousUnreadCount = prevUnreadCountRef.current;
    const unreadIncreased = totalUnreadCount > previousUnreadCount;

    if (unreadIncreased) {
      if (!Chat.isMobileMode) showNotification();
      const __notif = new Audio(notifSound);
      __notif.play().catch(() => {});
    }

    prevUnreadCountRef.current = totalUnreadCount;
  }, [totalUnreadCount, Chat.isMobileMode]);

  useEffect(() => {
    const appTitle = "Buildhub Internal Chat";
    const baseTitle = activeConversationName
      ? `${activeConversationName} - ${appTitle}`
      : appTitle;
    const unreadTitle = activeConversationName
      ? `(${totalUnreadCount}) ${activeConversationName} - New Messages | ${appTitle}`
      : `(${totalUnreadCount}) New Messages - ${appTitle}`;

    let intervalId = null;

    if (totalUnreadCount > 0) {
      let showUnreadTitle = true;
      document.title = unreadTitle;
      intervalId = setInterval(() => {
        showUnreadTitle = !showUnreadTitle;
        document.title = showUnreadTitle ? unreadTitle : baseTitle;
      }, 2000);
    } else {
      document.title = baseTitle;
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [totalUnreadCount, activeConversationName]);

  const handleRemoveFile = (filePreviewUrl) => {
    setFilePreview(
      filePreview.filter((file) => file.filePreview !== filePreviewUrl),
    );
    setSelectedFile(
      selectedFile.filter((file) => file.filePreview !== filePreviewUrl),
    );
  };

  const handleMessageInputPaste = (event) => {
    const clipboardItems = event.clipboardData.items;
    const dataTransfer = new DataTransfer();

    if (fileInputRef.current && fileInputRef.current.files.length > 0) {
      for (let i = 0; i < fileInputRef.current.files.length; i++) {
        dataTransfer.items.add(fileInputRef.current.files[i]);
      }
    }

    for (let i = 0; i < clipboardItems.length; i++) {
      const item = clipboardItems[i];

      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          dataTransfer.items.add(file);
        }
      }
    }

    if (dataTransfer.files.length > 0) {
      fileInputRef.current.files = dataTransfer.files;

      handleFileChange({
        target: {
          files: dataTransfer.files,
        },
      });
    }
  };

  const [reactionChatId, setReactionChatId] = useState(null);
  const [reactionGroupReaction, setReactionGroupReaction] = useState(null);
  const toggleShowReactionOptions = (chat_id, group_reaction) => {
    setShowReactionOptionList((prevState) => {
      const newState = !prevState;
      return newState;
    });

    setReactionChatId(chat_id);
    setReactionGroupReaction(group_reaction);
  };

  let lastDate = "";
  let lastSenderId = null;

  const [showPinMessageModal, setShowPinMessageModal] = useState(false);
  const [pinMessageData, setPinMessageData] = useState(null);
  const onClickPin = async (conversation_id, chat_id) => {
    setPinMessageData({ conversation_id, chat_id });
    setShowPinMessageModal(true);
  };

  const [privateMessageModalShow, setPrivateMessageModalShow] = useState(false);
  const [privateMessageModalData, setPrivateMesageModalData] = useState(null);

  const [showAiChatbot, setShowAiChatbot] = useState(false);

  const { tasks, toggleTask, pendingCount } = useAITasks();
  const [showAITasks, setShowAITasks] = useState(false);
  const [aiTaskToast, setAiTaskToast] = useState(null);
  const [showAiCommandHints, setShowAiCommandHints] = useState(false);
  const [showTigerZeroHints, setShowTigerZeroHints] = useState(false);
  mentionSelectRef.current = (option) => {
    if (option.id === "ai-cmd") {
      setShowAiCommandHints(true);
      setShowTigerZeroHints(false);
    } else if (option.id === "ai") {
      setShowTigerZeroHints(true);
      setShowAiCommandHints(false);
    }
  };

  const showAITaskToast = (msg) => {
    setAiTaskToast(msg);
    setTimeout(() => setAiTaskToast(null), 3500);
  };

  const handleToggleAiChatbot = (value) => {
    const next = typeof value === "boolean" ? value : !showAiChatbot;
    setShowAiChatbot(next);
    if (next && Chat.isMobileMode) {
      Chat.setMobileChatDetailShow(true);
    }
  };

  return (
    <main className="main">
      {conversationModalShow && (
        <ConversationModal
          showHideConversationModal={showHideConversationModal}
        />
      )}

      {privateMessageModalShow && (
        <SendPrivateMessageModal
          privateMessageModalData={privateMessageModalData}
          setPrivateMessageModalShow={setPrivateMessageModalShow}
          setPrivateMesageModalData={setPrivateMesageModalData}
        />
      )}
      {searchModalShow && (
        <SearchChatModal
          conversationData={Chat.conversationData?.conversation}
          showHideSearchmodal={showHideSearchmodal}
        />
      )}
      {viewMembersModal && (
        <ViewMembersModal
          conversationData={Chat.conversationData?.conversation}
          showHideViewMembersModal={showHideViewMembersModal}
          updateConversationData={Chat.updateConversationData}
          userDetails={userDetails}
          conversationUserPermission={Chat.conversationUserPermission}
        />
      )}
      {viewConversationFilesModal && (
        <ViewConversationFilesModal
          conversationData={Chat.conversationData?.conversation}
          showHideViewConversationFilesModal={
            showHideViewConversationFilesModal
          }
          userDetails={userDetails}
        />
      )}
      {viewActivityLogsShow && (
        <ViewActivityLogsModal
          conversationData={Chat.conversationData?.conversation}
          showHideViewActivityLogsModal={showHideViewActivityLogsModal}
          userDetails={userDetails}
        />
      )}
      {previewImage && (
        <PreviewImageModal
          previewImage={previewImage}
          isPreviewOpen={isPreviewOpen}
          closeModal={closeModal}
        />
      )}
      <ImageViewer
        openLightbox={openLightbox}
        setOpenLightbox={setOpenLightbox}
        lightboxSrcs={lightboxSrcs}
        activeIndex={lightboxActiveIndex}
        onIndexChange={setLightboxActiveIndex}
      />

      {viewChangePasswordModal && (
        <ChangePasswordModal
          showHideChangePasswordModal={showHideChangePasswordModal}
        />
      )}
      {editConversationModalShow && (
        <EditConversationModal
          showHideEditConversationModal={showHideEditConversationModal}
          conversationData={Chat.conversationData?.conversation}
        />
      )}
      {Chat.threadViewVisible && (
        <ViewThreadModal
          userId={userId}
          handleImageClick={handleImageClick}
          allowAiCommand={
            AI_CHAT_CONVERSATION_IDS.includes(Chat.storedConversationId) &&
            !!userDetails?.admin_access
          }
        />
      )}
      {Chat.reactionsViewVisible && <ViewReactionsModal />}
      {showPinMessageModal && (
        <PinModal
          setShowPinMessageModal={setShowPinMessageModal}
          pinMessageData={pinMessageData}
        />
      )}
      {showReactionOptionList && (
        <ReactionOptions
          receiver={true}
          chat_id={reactionChatId}
          reaction={reactionGroupReaction}
          userDetails={userDetails}
          toggleShowReactionOptions={toggleShowReactionOptions}
        />
      )}
      <div className="chat-container">
        <div className="chat">
          {/* SIDEBAR */}
          <SidebarHeader
            userDetails={userDetails}
            showHideChangePasswordModal={showHideChangePasswordModal}
            viewChangePasswordModal={viewChangePasswordModal}
            setPrivateMessageModalShow={setPrivateMessageModalShow}
            showAiChatbot={showAiChatbot}
            setShowAiChatbot={handleToggleAiChatbot}
            showAITasks={showAITasks}
            setShowAITasks={setShowAITasks}
            pendingTaskCount={pendingCount}
          />
          <div
            className={`chat__sidebar ${
              Chat.isMobileMode && !Chat.isMobileChatDetailShow
                ? "chat__sidebar__expand"
                : "chat__sidebar__collapsed"
            }`}
          >
            <div className="inbox">
              <div className="inbox__top">
                <div className="inbox__head">
                  <span className="inbox__heading">
                    <img
                      src={BuildhubTextLogo}
                      alt=""
                      className="inbox--headiing-image"
                    />
                  </span>
                  {/* <span className='inbox__unread-message-count js-unread-message-count'>{totalUnreadCount} New</span> */}
                  <button
                    className="inbox__add-btn"
                    onClick={() => setPrivateMessageModalShow(true)}
                  >
                    <i className="fa fa-plus inbox__add-icon"></i>
                  </button>
                </div>
                <SearchInput
                  showSearchResults={showSearchResults}
                  hideSearchResults={hideSearchResults}
                  isShowingSearchResults={isShowingSearchResults}
                  setGlobalSearchResults={setGlobalSearchResults}
                  setIsGlobalSearchLoading={setIsGlobalSearchLoading}
                  setHasGlobalSearchExecuted={setHasGlobalSearchExecuted}
                  setSearchQuery={setSearchQuery}
                />
                {!isShowingSearchResults && <ToggleSwitch />}
              </div>
              <div
                className="inbox__messages"
                style={{ backgroundImage: `url(${buildHubLogo})` }}
                id="inbox__messages"
              >
                <ul className="inbox__list" id="inbox__list">
                  {isShowingSearchResults ? (
                    isGlobalSearchLoading ? (
                      <div className="loader-container">
                        <span className="loader"></span>
                      </div>
                    ) : !searchQuery.trim() ? (
                      <li className="search-idle-state" role="status" aria-live="polite">
                        <i className="fa fa-search search-idle-state__icon"></i>
                        <p>Search conversations and messages</p>
                        <small>Type a keyword and press Enter</small>
                      </li>
                    ) : hasGlobalSearchExecuted &&
                      globalSearchResults &&
                      globalSearchResults.messages?.length === 0 &&
                      globalSearchResults.conversations?.length === 0 ? (
                      <li className="search-no-results">
                        <i className="fa fa-search search-no-results__icon"></i>
                        <p>No results for &ldquo;<strong>{searchQuery}</strong>&rdquo;</p>
                      </li>
                    ) : globalSearchResults ? (
                      <>
                        {globalSearchResults.conversations?.length > 0 && (
                          <>
                            <li className="search-section-header">
                              <span>Conversations</span>
                              <span className="search-section-header__count">{globalSearchResults.conversations.length}</span>
                            </li>
                            {globalSearchResults.conversations.map((conversation, index) => (
                              <ConversationSearchResult
                                conversation={conversation}
                                key={`conv-${index}`}
                                searchQuery={searchQuery}
                              />
                            ))}
                          </>
                        )}
                        {globalSearchResults.messages?.length > 0 && (
                          <>
                            <li className="search-section-header">
                              <span>Messages</span>
                              <span className="search-section-header__count">{globalSearchResults.messages.length}</span>
                            </li>
                            {globalSearchResults.messages.map((searchResult, index) => (
                              <SearchResults
                                searchResult={searchResult}
                                key={`msg-${index}`}
                                searchQuery={searchQuery}
                              />
                            ))}
                          </>
                        )}
                      </>
                    ) : (
                      <li className="search-idle-state" role="status" aria-live="polite">
                        <i className="fa fa-search search-idle-state__icon"></i>
                        <p>No search in progress</p>
                        <small>Press Enter to run your search</small>
                      </li>
                    )
                  ) : conversationProp.length > 0 ? (
                    <InfiniteScroll
                      dataLength={conversationProp.length}
                      next={conversationHandleViewMore}
                      hasMore={conversationsCount > conversationProp.length}
                      scrollableTarget="inbox__messages"
                      loader={<ChatSideBarItem loading={true} />}
                      style={{
                        overflow: "unset"
                      }}
                    >
                      {pinnedConversations.length > 0 && (
                        <li className="conversation-section-header conversation-section-header--pinned">
                          <span>
                            <i className="fa fa-thumb-tack" aria-hidden="true"></i>
                            Pinned
                          </span>
                          <span className="conversation-section-header__count">{pinnedConversations.length}</span>
                        </li>
                      )}
                      {pinnedConversations.map((chatDetail, index) => (
                        <ChatSideBarItem
                          chatDetail={chatDetail}
                          key={`pinned-${index}`}
                          linkConversation={linkConversation}
                          setConnectedRoom={setConnectedRoom}
                        />
                      ))}
                      {unpinnedConversations.length > 0 && (
                        <li className="conversation-section-header">
                          <span>All conversations</span>
                        </li>
                      )}
                      {unpinnedConversations.map((chatDetail, index) => (
                        <ChatSideBarItem
                          chatDetail={chatDetail}
                          key={`unpinned-${index}`}
                          linkConversation={linkConversation}
                          setConnectedRoom={setConnectedRoom}
                        />
                      ))}
                    </InfiniteScroll>
                  ) : (
                    Array.from({ length: 10 }).map((_, index) => (
                      <ChatSideBarItem id={index} key={index} loading={true} />
                    ))
                  )}
                </ul>
              </div>
            </div>
          </div>

          <div
            className={`chat__main ${
              Chat.isMobileMode && !Chat.isMobileChatDetailShow
                ? "chat__main__collapsed"
                : "chat__main__expand"
            }`}
          >
            {showAiChatbot ? (
              <AiChatbot
                onClose={() => handleToggleAiChatbot(false)}
                userDetails={userDetails}
              />
            ) : (
              <div className="message">
                <div className="message__head" id="message__head">
                  {/* MSG HEAD */}
                  <MessageHead
                    conversationData={Chat.conversationData}
                    setMobileChatDetailShow={Chat.setMobileChatDetailShow}
                    showHideSearchmodal={showHideSearchmodal}
                    showHideViewMembersModal={showHideViewMembersModal}
                    showHideViewConversationFilesModal={
                      showHideViewConversationFilesModal
                    }
                    showHideEditConversationModal={
                      showHideEditConversationModal
                    }
                    showHideViewActivityLogsModal={
                      showHideViewActivityLogsModal
                    }
                    conversationUserPermission={Chat.conversationUserPermission}
                  />
                </div>

                {shouldShowWsBanner && (
                  <div className={wsStatusClassName} role="status" aria-live="polite">
                    <span className="ws-status-dot" aria-hidden="true"></span>
                    <span>{wsStatusText}</span>
                  </div>
                )}

                {Chat.conversationData?.conversation?.pinned_message && (
                  <PinnedMessage
                    pinnedMessage={
                      Chat.conversationData?.conversation?.pinned_message
                    }
                  />
                )}

                {isJumpMode && (
                  <div className="jump-mode-banner" role="status" aria-live="polite">
                    <div className="jump-mode-banner__content">
                      <div className="jump-mode-banner__title">
                        <i className="fa fa-map-marker" aria-hidden="true"></i>
                        <span>Jump Mode: Viewing a focused message window</span>
                      </div>
                      <p className="jump-mode-banner__text">
                        You are not viewing the full timeline. Load older/newer in this window,
                        or exit to return to latest messages.
                      </p>
                      <div className="jump-mode-banner__chips">
                        <span
                          className={`jump-mode-chip ${
                            hasOlder ? "jump-mode-chip--ok" : "jump-mode-chip--edge"
                          }`}
                        >
                          {hasOlder
                            ? "Older messages available"
                            : "Oldest boundary reached"}
                        </span>
                        <span
                          className={`jump-mode-chip ${
                            hasNewer ? "jump-mode-chip--ok" : "jump-mode-chip--edge"
                          }`}
                        >
                          {hasNewer
                            ? "Newer messages available"
                            : "Newest boundary reached"}
                        </span>
                      </div>
                    </div>
                    <div className="jump-mode-banner__actions">
                      <button
                        type="button"
                        className="jump-mode-btn"
                        onClick={() => loadOlder()}
                        disabled={!hasOlder || isJumpLoadingOlder}
                      >
                        {isJumpLoadingOlder ? "Loading older..." : "Load older"}
                      </button>
                      <button
                        type="button"
                        className="jump-mode-btn"
                        onClick={() => loadNewer()}
                        disabled={!hasNewer || isJumpLoadingNewer}
                      >
                        {isJumpLoadingNewer ? "Loading newer..." : "Load newer"}
                      </button>
                      <button
                        type="button"
                        className="jump-mode-btn jump-mode-btn--primary"
                        onClick={handleExitJumpMode}
                      >
                        Exit Jump Mode
                      </button>
                    </div>
                  </div>
                )}

                <div
                  className="message__main"
                  ref={Chat.messageMainRef}
                  id="scrollableDiv"
                  style={{
                    height: divHeight,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    backgroundImage: `url(${buildHubLogo})`,
                  }}
                >
                  <InfiniteScroll
                    dataLength={allItems.length}
                    next={handleViewMore}
                    hasMore={false}
                    scrollableTarget="scrollableDiv"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {allItems?.length > 0 ? (
                      <>
                        {/* MSG CONTAINER */}
                        {allItems.map((chat, index) => {
                          const currentDate = formatDate(chat.createdAt);
                          const showSeparator = currentDate !== lastDate;
                          lastDate = currentDate;
                          if (showSeparator) {
                            lastSenderId = null;
                          }
                          const showSenderName =
                            !chat.sender_id || chat.sender_id !== lastSenderId;
                          const isLastInSenderGroup = isLastMessageInSenderGroup(
                            allItems,
                            index,
                            currentDate,
                          );
                          if (chat.sender_id) {
                            lastSenderId = chat.sender_id;
                          }
                          return (
                            <React.Fragment key={chat.id ?? index}>
                              {showSeparator && (
                                <MessageSeparator date={currentDate} />
                              )}
                              {renderChats(
                                chat,
                                index,
                                userId,
                                userDetails,
                                handleImageClick,
                                toggleShowChatReply,
                                Chat.setChatReplyData,
                                toggleShowReactionOptions,
                                onClickPin,
                                setPrivateMessageModalShow,
                                setPrivateMesageModalData,
                                showSenderName,
                                isLastInSenderGroup,
                              )}
                            </React.Fragment>
                          );
                        })}
                        {/* MSG CONTAINER */}
                        {!isAtBottom && (
                          <button
                            className="scroll-to-bottom"
                            onClick={scrollToBottom}
                          >
                            <i className="fa fa-chevron-down"></i>
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="loader-container-message--main">
                          <h2>Empty Chat</h2>
                          <small>
                            Choose a conversation or send something!
                          </small>
                          {/* <span className='loader-message--main'></span> */}
                        </div>
                      </>
                    )}
                    {Chat.isShowingTyping && Chat.typingData ? (
                      <MessageTyping
                        typingData={Chat.typingData}
                        userDetails={userDetails}
                      />
                    ) : null}
                  </InfiniteScroll>
                </div>

                {/* CHAT INPUT REPLY */}
                {Chat.chatReplyData !== null && (
                  <ChatInputReply
                    toggleShowChatReply={toggleShowChatReply}
                    chatReplyData={Chat.chatReplyData}
                    setChatReplyData={Chat.setChatReplyData}
                    userDetails={userDetails}
                  />
                )}
                {/* CHAT INPUT REPLY */}
                {/* AI COMMAND HINTS — shown after user selects @AI from dropdown */}
                {showAiCommandHints && (
                  <AICommandHints
                    onSelectHint={handleSelectAiHint}
                    onDismiss={() => setShowAiCommandHints(false)}
                  />
                )}
                {showTigerZeroHints && (
                  <TigerZeroHints
                    onSelectHint={handleSelectTigerHint}
                    onDismiss={() => setShowTigerZeroHints(false)}
                  />
                )}
                <div className="message__input-container">
                  <div className="user-message-attachment-container">
                    {filePreview.map((_filePreview, index) => {
                      if (_filePreview.type.includes("image/")) {
                        return (
                          <div key={index}>
                            <img src={_filePreview.filePreview} alt="" />
                            <button
                              className="message__btn-remove-file"
                              onClick={() =>
                                handleRemoveFile(_filePreview.filePreview)
                              }
                            >
                              <i className="fa fa-times"></i>
                            </button>{" "}
                          </div>
                        );
                      } else {
                        return (
                          <div key={index}>
                            <div className="message__file-name">
                              {_filePreview.file.name}
                            </div>
                            <button
                              className="message__btn-remove-file"
                              onClick={() =>
                                handleRemoveFile(_filePreview.filePreview)
                              }
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          </div>
                        );
                      }
                    })}
                  </div>
                  <div className="mention-composer">
                    {mention.pickerOpen && (
                      <MentionPicker
                        query={mention.mentionSession.query}
                        options={mention.mentionOptions}
                        activeIndex={mention.activeIndex}
                        onActiveIndexChange={mention.setActiveIndex}
                        onSelect={mention.handleSelect}
                        listId="mention-picker-main"
                      />
                    )}
                    <div className="message-input----container">
                    <MentionComposerField
                      textareaRef={msgInputTextAreaRef}
                      style={{ height: "45px" }}
                      className="message__input js-message--input msg-tool"
                      placeholder={
                        !Chat.isPermittedToChat
                          ? "You are restricted from chatting in this conversation"
                          : "Write a message..."
                      }
                      disabled={!Chat.isPermittedToChat}
                      value={messageContent}
                      autoComplete="off"
                      aria-autocomplete="list"
                      aria-expanded={mention.pickerOpen}
                      aria-controls={
                        mention.pickerOpen ? "mention-picker-main" : undefined
                      }
                      aria-activedescendant={
                        mention.pickerOpen
                          ? `mention-picker-main-option-${mention.activeIndex}`
                          : undefined
                      }
                      onChange={(e) => handleMessageInput(e)}
                      onKeyDown={(e) => {
                        if (mention.handleKeyDown(e)) return;
                        if (
                          e.key === "Enter" &&
                          e.shiftKey &&
                          Chat.isPermittedToChat
                        ) {
                          e.preventDefault();
                          setMessageContent(
                            (prevContent) => prevContent + "\n",
                          );
                        } else if (
                          e.key === "Enter" &&
                          Chat.isPermittedToChat &&
                          !Chat.isMobileMode
                        ) {
                          e.preventDefault();
                          handleSendMessage();
                        } else {
                          handleCurrentlyTyping();
                        }
                      }}
                      onKeyUp={mention.handleCaretMove}
                      onClick={mention.handleCaretMove}
                      onBlur={mention.handleBlur}
                      onFocus={mention.handleFocus}
                      onPaste={handleMessageInputPaste}
                    />

                    <div className="message-input----container-actions">
                      <input
                        type="file"
                        id="fileInput"
                        className="message__file-input"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        multiple
                      />

                      <button
                        className="message__btn-file js-file-upload--btn msg-tool"
                        onClick={() =>
                          document.getElementById("fileInput").click()
                        }
                      >
                        <i className="fa fa-paperclip message__btn-file-icon"></i>
                      </button>

                      <button
                        className="message__btn-send js-send-message--btn msg-tool"
                        data-receiver=""
                        data-cid=""
                        onClick={() => {
                          if (Chat.isPermittedToChat) {
                            handleSendMessage();
                          }
                        }}
                      >
                        <i className="fa fa-paper-plane message__btn-send-icon"></i>
                        <img src={loader2} className="loader-msg" />
                      </button>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AITasksPanel
        isOpen={showAITasks}
        tasks={tasks}
        onToggle={toggleTask}
        onClose={() => setShowAITasks(false)}
      />
      {aiTaskToast && (
        <div className="ai-task-toast">
          <i className="fa fa-check-circle" style={{ marginRight: 8 }} />
          {aiTaskToast}
        </div>
      )}
    </main>
  );
}

function isLastMessageInSenderGroup(items, index, currentDate) {
  const current = items[index];
  if (!current?.sender_id) return true;

  for (let i = index + 1; i < items.length; i += 1) {
    const next = items[i];
    if (formatDate(next.createdAt) !== currentDate) return true;
    if (next?.sender_id) return next.sender_id !== current.sender_id;
  }

  return true;
}

function renderChats(
  item,
  index,
  currentUserId,
  userDetails,
  handleImageClick,
  toggleShowChatReply,
  setChatReplyData,
  toggleShowReactionOptions,
  onClickPin,
  setPrivateMessageModalShow,
  setPrivateMesageModalData,
  showSenderName,
  isLastInSenderGroup,
) {
  const { createdAt } = item;

  if (item.sender_id) {
    const { sender_id } = item;
    return (
      <React.Fragment key={createdAt + index}>
        {sender_id === currentUserId ? (
          <MessageSender
            messageDetail={item}
            handleImageClick={handleImageClick}
            toggleShowChatReply={toggleShowChatReply}
            setChatReplyData={setChatReplyData}
            userDetails={userDetails}
            toggleShowReactionOptions={toggleShowReactionOptions}
            onClickPin={onClickPin}
          />
        ) : (
          <MessageReceiver
            messageDetail={item}
            handleImageClick={handleImageClick}
            toggleShowChatReply={toggleShowChatReply}
            setChatReplyData={setChatReplyData}
            userDetails={userDetails}
            toggleShowReactionOptions={toggleShowReactionOptions}
            onClickPin={onClickPin}
            setPrivateMessageModalShow={setPrivateMessageModalShow}
            setPrivateMesageModalData={setPrivateMesageModalData}
            showSenderName={showSenderName}
            isLastInSenderGroup={isLastInSenderGroup}
          />
        )}
      </React.Fragment>
    );
  }
}

export default Main;
